import { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { responsiveImageInputs } from "@/libs/builder/common/responsiveImageInputs";
import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const welcomePopupInputs = [
  {
    name: "logo",
    type: "object",
    required: true,
    subFields: [
      {
        name: "src",
        type: "file",
        required: true,
        allowedFileTypes: IMAGE_FILE_TYPES,
      },
      {
        name: "alt",
        type: "string",
      },
    ],
    defaultValue: {
      alt: "Logo",
      src: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F9d1d2ffe26704533a8ad5fbf1abac751",
    },
  },
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Welcome to [BRAND]",
  },
  {
    name: "paragraph",
    type: "object",
    required: true,
    subFields: [
      {
        name: "content",
        type: "richText",
        required: true,
      },
    ],
    defaultValue: {
      content:
        "[Ploom domain.com] is limited to adult smokers. To access exclusive content and personalized experiences, please log in or sign up for an account. By joining, you'll unlock a world of benefits, including offers, free shipping, and more.",
    },
  },
  {
    name: "cta",
    type: "object",
    required: true,
    subFields: [
      {
        name: "text",
        type: "string",
        required: true,
      },
      {
        name: "secondaryText",
        type: "object",
        required: true,
        subFields: [
          {
            name: "content",
            type: "richText",
            required: true,
          },
        ],
      },
    ],
    defaultValue: {
      text: "Login",
      secondaryText: {
        content: "Don't have an account? <a href='/'>Create an account</a>",
      },
    },
  },
  {
    name: "legalNotice",
    type: "object",
    required: true,
    subFields: [
      {
        name: "content",
        type: "richText",
        required: true,
      },
    ],
    defaultValue: {
      content:
        "Your privacy is important to us. Read our <a href='/'>[Privacy policy]</a> and <a href='/'>[Terms of service]</a> to learn more about how we protect your data.",
    },
  },
  {
    name: "image",
    type: "object",
    required: true,
    subFields: responsiveImageInputs,
    defaultValue: {
      alt: "Welcome image",
      title: "Welcome image",
      largeSrc:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F24847fec13674857a19141d1480b9ed2",
    },
  },
  {
    name: "socialMediaLinks",
    type: "list",
    subFields: [
      {
        name: "link",
        type: "url",
        required: true,
        defaultValue: "#",
      },
      {
        name: "trackingName",
        type: "string",
        required: true,
        defaultValue: "Tracking name",
      },
      {
        name: "image",
        type: "object",
        required: true,
        subFields: [
          {
            name: "src",
            type: "file",
            required: true,
          },
          {
            name: "alt",
            type: "string",
            required: true,
          },
        ],
      },
    ],
  },
  {
    name: "socialMediaText",
    type: "string",
    defaultValue: "Contact us if you have any question",
  },
  {
    name: "backgroundColor",
    type: "string",
    enum: [
      "white",
      "black",
      "softBlack",
      "blurredWhite",
      "blurredBlack",
      "blurredSoftBlack",
    ],
    defaultValue: "blurredWhite",
  },
] as const satisfies Inputs;

export const WelcomePopupComponent: RegisteredComponent = {
  name: "Welcome Popup",
  component: dynamic(
    async () =>
      withValidation(
        welcomePopupInputs,
        (await import("./WelcomePopup")).WelcomePopup,
        "WelcomePopup"
      ),
    { ssr: false }
  ),
  models: [getBuilderModelName("welcome-popup")],
  inputs: welcomePopupInputs,
  canHaveChildren: true,
  defaultChildren: [],
  childRequirements: {
    message: "You can only put HealthBanner component inside this component",
    query: {
      "component.name": {
        $in: ["HealthBanner"],
      },
    },
  },
};
